import { memo, useState } from 'react';
import { Modal, Select, DatePicker, Space, message } from 'antd';
import dayjs from 'dayjs';
import { CollectItemAPIs, CollectItem_Export_RequestQuery } from '~/common/typescript/apis/CollectItemAPIs';
import { StreamSaverUtils } from '~/utils/StreamSaverUtils';
import { API_URL_ADMIN } from '~/config/config';

interface ExportModalProps {
  visible: boolean;
  onClose: () => void;
  collectFormUuid: string | null;
  collectItemsCount: number;
}

const ExportModal = ({
  visible,
  onClose,
  collectFormUuid,
  collectItemsCount,
}: ExportModalProps) => {
  const [exportLoading, setExportLoading] = useState(false);
  const [exportForm, setExportForm] = useState<{
    format: 'ESRI Shapefile' | 'MapInfo File' | 'LIBKML' | 'XLSX' | 'GeoJSON';
    dateRange: [string, string];
  }>(() => ({
    format: 'GeoJSON',
    dateRange: [
      dayjs().startOf('year').format('YYYY-MM-DD'),
      dayjs().endOf('year').format('YYYY-MM-DD'),
    ],
  }));

  const handleExport = async () => {
    if (!collectFormUuid) {
      message.error('Chưa chọn biểu mẫu để xuất dữ liệu');
      return;
    }

    setExportLoading(true);
    try {
      const request: CollectItem_Export_RequestQuery = {
        form_uuid: collectFormUuid,
        format: exportForm.format,
        client_created_at_start: exportForm.dateRange[0],
        client_created_at_end: exportForm.dateRange[1],
      };

      const response = await CollectItemAPIs.remote_exportCollectItems(request);

      const extensions: Record<string, string> = {
        'ESRI Shapefile': 'zip',
        'MapInfo File': 'zip',
        LIBKML: 'kml',
        XLSX: 'xlsx',
        GeoJSON: 'geojson',
      };

      const extension = extensions[exportForm.format];
      const filename = `collect_items_${exportForm.format.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}.${extension}`;

      const downloadUrl = `${API_URL_ADMIN}${response.data.download_url}`;
      await StreamSaverUtils.download(downloadUrl, filename);

      message.success(`Đã tải xuống ${response.data.file_size} bytes dữ liệu định dạng ${exportForm.format}`);
      onClose();
    } catch (error: any) {
      console.error('Export error:', error);

      if (error?.response?.status === 404) {
        message.warning(
          error?.response?.data?.i18n?.vi || 'Không tìm thấy dữ liệu để xuất trong khoảng thời gian đã chọn',
        );
      } else {
        message.error(error?.response?.data?.message || error?.response?.data?.i18n?.vi || 'Lỗi khi xuất dữ liệu');
      }
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Modal
      title="Tải xuống dữ liệu thu thập"
      open={visible}
      onOk={handleExport}
      onCancel={onClose}
      confirmLoading={exportLoading}
      okText="Tải xuống"
      cancelText="Hủy"
      width={500}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>
            Định dạng file:
          </label>
          <Select
            value={exportForm.format}
            onChange={(format) => setExportForm({ ...exportForm, format })}
            style={{ width: '100%' }}
            options={[
              { label: 'GeoJSON (.geojson)', value: 'GeoJSON' },
              { label: 'ESRI Shapefile (.zip)', value: 'ESRI Shapefile' },
              { label: 'MapInfo File (.zip)', value: 'MapInfo File' },
              { label: 'KML (.kml)', value: 'LIBKML' },
              { label: 'Excel (.xlsx)', value: 'XLSX' },
            ]}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>
            Khoảng thời gian tạo:
          </label>
          <Space direction="vertical" style={{ width: '100%' }}>
            <DatePicker
              data-testid="export-date-start"
              placeholder="Từ ngày"
              value={exportForm.dateRange[0] ? dayjs(exportForm.dateRange[0]) : null}
              onChange={(date) => {
                if (date) {
                  const dateStr = date.format('YYYY-MM-DD');
                  setExportForm({
                    ...exportForm,
                    dateRange: [dateStr, exportForm.dateRange[1]],
                  });
                }
              }}
              style={{ width: '100%' }}
            />
            <DatePicker
              data-testid="export-date-end"
              placeholder="Đến ngày"
              value={exportForm.dateRange[1] ? dayjs(exportForm.dateRange[1]) : null}
              onChange={(date) => {
                if (date) {
                  const dateStr = date.format('YYYY-MM-DD');
                  setExportForm({
                    ...exportForm,
                    dateRange: [exportForm.dateRange[0], dateStr],
                  });
                }
              }}
              style={{ width: '100%' }}
            />
          </Space>
        </div>
      </Space>
    </Modal>
  );
};

export default memo(ExportModal);
